import { useState, useRef, FC, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled, { createGlobalStyle } from "styled-components";
import { CorporateTheme } from "../../utils/themes";
import CalendarIcon from "../../assets/icons/CalendarIcon";
import { useTheme } from "../../middleware/ThemeContext";
import CustomInput from '../form/CustomInput';
import { FieldLabel } from "../FormBuilder/FieldSettings/FieldSettingsModal";
import { formatDateDefault } from "../../utils/helper";
import ResetIcon from "../../assets/icons/ResetIcon";
import CloseIcon from "../../assets/icons/CloseIcon";
import { ResetButton } from "./Filters";
import StyledText from "../StyledComponents/StyledText";

interface DateRangeFilterProps {
  onDateChange: (dates: [Date | null, Date | null]) => void;
  startDate: Date | null;
  endDate: Date | null;
  label?: string;
  position?: 'left' | 'right';
  routeKey?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  showResetButton?: boolean;
  placeholder?: string;
  dateRangeClassname?: string;
}

const DatePickerGlobalStyles = createGlobalStyle(({ theme }) => `
  .react-datepicker {
    border: 1px solid ${theme.CorporateTheme.colors.border} !important;
    font-family: 'Raleway', sans-serif !important;
    background-color: transparent !important;

    &__header {
      background-color: transparent !important;
      border-bottom: 1px solid ${theme.CorporateTheme.colors.border} !important;
    }

    &__day-name {
      color: ${theme.CorporateTheme.colors.textSecondary} !important;
      font-weight: 500 !important;
    }

    &__day {
      color: ${theme.CorporateTheme.colors.whiteBlack} !important;

      &:hover {
        background-color: ${theme.CorporateTheme.colors.border} !important;
      }

      &--selected {
        bac
      color: ${theme.CorporateTheme.colors.whiteBlack} !important;
      font-weight: 600 !important;
    }
kground-color: ${theme.CorporateTheme.colors.border} !important;
        color: ${theme.CorporateTheme.colors.whiteBlack} !important;
      }

      &--keyboard-selected {
        background-color: ${theme.CorporateTheme.colors.border} !important;
      }
    }

    &__current-month {
    &__navigation-icon::before {
      border-color: ${theme.CorporateTheme.colors.whiteBlack} !important;
    }
  }
      .react-datepicker__year-read-view--selected-year {
    font-weight: bold !important;
    font-size: 1rem !important;
    color: ${theme.CorporateTheme.colors.whiteBlack} !important;
  }
    .react-datepicker__year-read-view--down-arrow, .react-datepicker__month-read-view--down-arrow, .react-datepicker__month-year-read-view--down-arrow {
    margin-top: 5px !important;
}
.react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header {
color: ${theme.CorporateTheme.colors.whiteBlack} !important;
}
`);

const Label = styled('span')(({ theme }) => ({
  color: theme.CorporateTheme.colors.headerColor,
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: '"Raleway", sans-serif',
  whiteSpace: 'nowrap',
  backgroundColor: theme.CorporateTheme.colors.labelBG,
  padding: CorporateTheme.spacing.sm,
}));

const DateFilterContainer = styled.div`
  // display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const Dropdown = styled('div')<{ position?: 'left' | 'right' }>(({ theme, position }) => ({
  position: 'absolute',
  top: '100%',
  right: position === 'right' ? 'auto' : '0',
  left: position === 'right' ? '0' : 'auto',
  marginTop: CorporateTheme.spacing.sm,
  background: theme.CorporateTheme.colors.background,
  border: `1px solid ${theme.CorporateTheme.colors.border}`,
  borderRadius: CorporateTheme.borderRadius,
  padding: CorporateTheme.spacing.md,
  boxShadow: `0 4px 12px ${theme.CorporateTheme.shadow}`,
  zIndex: 1000,
}));

const DateFilterButton = styled.button<{ $active?: boolean, disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start; // or space-between if you want spread
  gap: ${CorporateTheme.spacing.sm};
  padding: ${CorporateTheme.spacing.sm};
  border-radius: ${CorporateTheme.borderRadius};
  background: ${({ theme }) => theme.colors.contentBackground};
  border: 1.5px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  cursor:  ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? '0.5' : '1'};
  color: ${({ theme }) => theme.colors.contentText};
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  width: 100%;   // 🔥 makes it stretch

  &:focus {
    outline: none;
  }

  &:hover {
    background: ${({ theme, disabled }) => disabled ? 'transparent' : theme.colors.topBarsColorHover};
  }


`;


const DateInputContainer = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  margin-bottom: ${CorporateTheme.spacing.md};
  gap: ${CorporateTheme.spacing.lg};
`;

const DateInputsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: CorporateTheme.spacing.sm,
  width: '100%',
  alignItems: 'center',
  border: `2px solid ${theme.CorporateTheme.colors.border}`,
  borderRadius: CorporateTheme.borderRadius,
}));

const DatePickersContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1;
  // margin-bottom: ${CorporateTheme.spacing.md};
  gap: ${CorporateTheme.spacing.lg};
`;

const DatePickersWrapper = styled.div`
  display: flex;
  gap: ${CorporateTheme.spacing.sm};
  width: 100%;
  align-items: center;
`;

const DateInput = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: CorporateTheme.spacing.sm,
  padding: CorporateTheme.spacing.xs,
  cursor: 'pointer',
  transition: 'border-color 0.2s',

  '&:placeholder': {
    color: theme.CorporateTheme.colors.textSecondary,
    fontWeight: 500,
  },

  '&:hover': {
    borderColor: theme.CorporateTheme.colors.accent,
  },

  input: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: '"Raleway", sans-serif',
    width: '100%',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.CorporateTheme.colors.textSecondary,
    fontWeight: 500,
  },
}));

const DateRangeFilter: FC<DateRangeFilterProps> = ({
  onDateChange,
  startDate,
  endDate,
  label,
  position,
  hideLabel,
  disabled,
  showResetButton = false,
  placeholder = 'Select Date',
  dateRangeClassname
}) => {
  const { theme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ✅ Local state to track selections until both are picked
  const [localStartDate, setLocalStartDate] = useState<Date | null>(startDate);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(endDate);

  // Sync prop updates with internal state
  useEffect(() => {
    // Validate date before setting
    if (startDate && !isNaN(startDate.getTime())) {
      setLocalStartDate(startDate);
    } else if (!startDate) {
      setLocalStartDate(null);
    }
  }, [startDate]);

  useEffect(() => {
    // Validate date before setting
    if (endDate && !isNaN(endDate.getTime())) {
      setLocalEndDate(endDate);
    } else if (!endDate) {
      setLocalEndDate(null);
    }
  }, [endDate]);

  const handleStartChange = (date: Date | null) => {
    setLocalStartDate(date);
    onDateChange([date, localEndDate]);
  };

  const handleEndChange = (date: Date | null) => {
    setLocalEndDate(date);
    onDateChange([localStartDate, date]);
    // Only close dropdown if both dates are valid and selected
    // if (date && localStartDate && !isNaN(date.getTime()) && !isNaN(localStartDate.getTime())) {
    //   setIsDropdownOpen(false);
    // }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  const displayRange =
    localStartDate && localEndDate && !isNaN(localStartDate.getTime()) && !isNaN(localEndDate.getTime())
      ? `${formatDateDefault(localStartDate)} - ${formatDateDefault(localEndDate)}`
      : localStartDate && !isNaN(localStartDate.getTime())
        ? `${formatDateDefault(localStartDate)} to ...`
        : label ?? placeholder;

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dateHandler = () => {
    setIsDropdownOpen(!isDropdownOpen)
  };

  const handleReset = () => {
    setLocalStartDate(null);
    setLocalEndDate(null);
    onDateChange([null, null]); // parent ko bhi update kar do
    setIsDropdownOpen(false);
  };


  return (
    <DateFilterContainer ref={containerRef}>
      <DatePickerGlobalStyles theme={theme} />
      {!hideLabel ? <FieldLabel theme={theme}>
        {label}
      </FieldLabel> : null}
      <div className="d-flex align-items-center gap-2">
        <DateFilterButton
          onClick={dateHandler}
          $active={isDropdownOpen}
          theme={theme}
          disabled={disabled}
          className={dateRangeClassname}
        >
          <CalendarIcon color={theme.CorporateTheme.colors.headerColor} />
          <StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>{displayRange}</StyledText>
        </DateFilterButton>
        {showResetButton && (localStartDate || localEndDate) && (
          <ResetButton
            onClick={handleReset}
            theme={theme}
          >
            <CloseIcon color={theme.CorporateTheme.colors.headerColor} />
          </ResetButton>
        )}

      </div>
      {isDropdownOpen && (
        <Dropdown theme={theme} position={position || 'left'}>
          {/* <DateInputContainer>
            <DateInputsWrapper theme={theme}>
              <Label theme={theme}>From</Label>
              <DateInput theme={theme}>
                <CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />
                <input
                  placeholder="Select starting date"
                  readOnly
                  value={localStartDate?.toLocaleDateString() || ""}
                />
              </DateInput>
            </DateInputsWrapper>
            <DateInputsWrapper theme={theme}>
              <Label theme={theme}>To</Label>
              <DateInput theme={theme}>
                <CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />
                <input
                  placeholder="Select ending Date"
                  readOnly
                  value={localEndDate?.toLocaleDateString() || ""}
                />
              </DateInput>
            </DateInputsWrapper>
          </DateInputContainer> */}

          <DatePickersContainer>
            <DatePickersWrapper>
              <Label theme={theme}>From</Label>
              <CustomInput
                type="date"
                rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                placeholder="Select date"
                value={localStartDate && !isNaN(localStartDate.getTime()) ? localStartDate.toISOString().split('T')[0] : ""}
                onChange={(value: string) => handleStartChange(value ? new Date(value) : null)}
              />
            </DatePickersWrapper>
            <DatePickersWrapper>
              <Label theme={theme}>To</Label>
              <CustomInput
                type="date"
                rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                placeholder="Select date"
                value={localEndDate && !isNaN(localEndDate.getTime()) ? localEndDate.toISOString().split('T')[0] : ""}
                onChange={(value: string) => handleEndChange(value ? new Date(value) : null)}
              />
            </DatePickersWrapper>
          </DatePickersContainer>
        </Dropdown>
      )}
    </DateFilterContainer>
  );
};

export default DateRangeFilter;
